/* eslint-disable @typescript-eslint/naming-convention */
import { IPaymentOption } from '../../IApiResponses';
import { PaymentProvider, IAfterpayLimis } from '../../../domain/entities/Payment/Payment';
import PAYMENT_PROVIDERS, { PAYMENT_MODES } from '../../../constants/PaymentProviders';
import { IPayPalSettings } from '../../../domain/entities/Config';
import { enhancePaymentName } from './paymentNameEnhance';

const enhancePaymentMode = (provider: string, paymentMode: string): string => {
  // need backend to add CC payment_mode in all brands - reference ticket ID: RCO-4840
  if (provider === PAYMENT_PROVIDERS.ADYEN_WEB && !paymentMode) {
    return PAYMENT_MODES.CREDIT_CARD;
  }

  return paymentMode;
};

type Maybe<T> = T | undefined;
const convertToBooleanWhenConfigured = (value: Maybe<number>) : Maybe<boolean> =>
  value === undefined ? undefined : !!value;
  
const paymentOptionsMapper = {
  response(paymentOptions: IPaymentOption[]): PaymentProvider[] {
    return paymentOptions.map(paymentItem => {
      const {
        accepted_cash_options: acceptedCashOptions,
        accepted_credit_cards: acceptedCreditCards,
        after_pay_limits,
        api_url,
        client_key: clientKey,
        logo: logoImgPath,
        payment_mode: paymentMode,
        payment_option: paymentOption,
        payment_type: paymentType,
        pp_name: providerName,
        provider_locale: providerLocale,
        valid_with_giftcard,
        is_default,
        is_available,
        qty_allowed: maxQuantity,
        adyen_config_obj_environment: adyenWebEnvironment,
        enable_cc_save_set_default: enableCCSaveSetDefault,
        enable_installments: enableInstallments,
        number_of_installments: installmentsNumber,
        intro_image: introImgPath,
        disable_store_details: disableCCSaveNextPayment,
        TEST_SERVER: isTestServer,
        has_card_holder_name,
        hide_cvc,
        require_card_holder_name,
        grey_out_if_unavailable,
        grey_out_logo,
      } = paymentItem;
      const sdks = [];
      const payLaterEligible = convertToBooleanWhenConfigured(paymentItem?.pay_later_eligible);
      const venmoProfileId = paymentItem?.profile_id;
      let payPalSettings: IPayPalSettings;
      if (api_url?.sdk_js) {
        sdks.push({
          integrity: api_url.integrity,
          src: api_url.sdk_js,
        });
      }
      let afterpayLimits: IAfterpayLimis;
      if (providerName === PAYMENT_PROVIDERS.AFTERPAY) {
        afterpayLimits = {
          maxValue: after_pay_limits?.afterpay_limit_max,
          minValue: after_pay_limits?.afterpay_limit_min,
        };
        sdks.push({
          src: paymentItem.JSURL,
        });
      }
      if (paymentItem.paypal_settings) {
        payPalSettings = {
          ...paymentItem.paypal_settings,
          commit: !!paymentItem.paypal_settings.commit,
        };
      }

      return {
        acceptedCashOptions: acceptedCashOptions || [],
        acceptedCreditCards: acceptedCreditCards || [],
        adyenWebEnvironment,
        afterpayLimits,
        clientKey,
        disableCCSaveNextPayment: !!disableCCSaveNextPayment,
        disabledLogoImgPath: grey_out_logo,
        enableCCSaveSetDefault: !!enableCCSaveSetDefault,
        enableInstallments: !!enableInstallments,
        hasHolderName: convertToBooleanWhenConfigured(has_card_holder_name),
        hideCVC: convertToBooleanWhenConfigured(hide_cvc),
        installmentsNumber,
        introImgPath,               
        isAvailable: !!is_available,
        isDefault: !!is_default,
        isGiftCardSupported: !!valid_with_giftcard,
        isHolderNameRequired: convertToBooleanWhenConfigured(require_card_holder_name),
        isTestServer: !!isTestServer,
        logoImgPath,
        maxQuantity,
        payLaterEligible,
        payPalSettings,
        paymentMode: enhancePaymentMode(providerName, paymentMode),
        paymentOption,
        paymentType,
        providerLocale,
        providerName: enhancePaymentName(providerName, paymentMode),    
        sdks,
        showAsDisabled: !is_available && !!grey_out_if_unavailable,
        venmoProfileId,
      };
    });
  },
};

export default paymentOptionsMapper;
